import groupBy from 'lodash-es/groupBy';
import keyBy from 'lodash-es/keyBy';
import omit from 'lodash-es/omit';
import { uniqPrimativeMerge } from '../../utils/arrayUtils';
import { LogisticsActionKeys as keys } from './logisticsActionKeys';
import { LogisticsActionTypes } from './logisticsActionTypes';
import * as i from './logisticsInterfaces';

export interface ILogisticsState {
  calendarFilters: i.ILogisticsFilters;
  carriers: Record<string, i.ICarrier>;
  containerHash: { [index: number]: i.IShipmentContainer };
  dailySummaryHash: Record<number, i.ILogisticsDailySummary>;
  dashboardFilters: i.ILogisticsFilters;
  events: Record<string, i.ILoadEvent[]>;
  itemHash: Record<number, i.IShipmentItem>;
  loading: string[];
  loads: Record<string, i.ILoadNormalized | undefined>;
  minLoads: Record<string, i.ILoadMin>;
  optimisticLoadUpdates: Record<string, Partial<i.ILoadNormalized>>;
  routes: Record<string, i.ILoadRoute>;
  shipmentHash: Record<number, i.IShipment>;
  shipmentListItemHash: Record<number, i.IShipmentListItem>;
  visibleShipmentListItems: number[];
}

const initialState: ILogisticsState = {
  calendarFilters: { company: '', warehouses: [] },
  carriers: {},
  containerHash: {},
  dailySummaryHash: {},
  dashboardFilters: { company: '', warehouses: [] },
  events: {},
  itemHash: {},
  loading: [],
  loads: {},
  minLoads: {},
  optimisticLoadUpdates: {},
  routes: {},
  shipmentHash: {},
  shipmentListItemHash: {},
  visibleShipmentListItems: []
};

export function logisticsReducer(state: ILogisticsState = initialState, action: LogisticsActionTypes): ILogisticsState {
  switch (action.type) {
    case keys.SET_SHIPMENT:
      const { shipment, containerHash, itemHash } = normalizeShipment(action.payload);
      return {
        ...state,
        containerHash: {
          ...state.containerHash,
          ...containerHash
        },
        itemHash: {
          ...state.itemHash,
          ...itemHash
        },
        loads: {
          ...state.loads,
          [shipment.loadNumber]: state.loads[shipment.loadNumber]
            ? {
                ...state.loads[shipment.loadNumber]!,
                shipmentIds: uniqPrimativeMerge(state.loads[shipment.loadNumber]!.shipmentIds, shipment.shipmentId)
              }
            : undefined
        },
        shipmentHash: {
          ...state.shipmentHash,
          [shipment.shipmentId]: shipment
        }
      };
    case keys.FETCH_SHIPMENT_LIST:
      return {
        ...state,
        visibleShipmentListItems: []
      };
    case keys.FETCH_SHIPMENT_LIST_FULFILLED:
      return {
        ...state,
        shipmentListItemHash: {
          ...state.shipmentListItemHash,
          ...keyBy(action.payload, (x: i.IShipmentListItem) => x.shipmentId)
        },
        visibleShipmentListItems: action.payload.map(x => x.shipmentId)
      };
    case keys.REMOVE_LOAD:
      return {
        ...state,
        loads: omit(state.loads, action.payload.loadNumber),
        minLoads: omit(state.minLoads, action.payload.loadNumber),
        routes: omit(state.routes, action.payload.loadNumber)
      };
    case keys.REMOVE_LOAD_ROUTE:
      return {
        ...state,
        routes: omit(state.routes, action.payload.loadNumber)
      };
    case keys.REMOVE_SHIPMENT:
      return removeShipment(state, action.payload);
    case keys.REMOVE_SHIPMENT_CONTAINER:
      const originalContainer = state.containerHash[action.payload.shipmentContainerId];

      return {
        ...state,
        containerHash: omit(state.containerHash, action.payload.shipmentContainerId),
        shipmentHash:
          originalContainer && state.shipmentHash[originalContainer.shipmentId]
            ? {
                ...state.shipmentHash,
                [originalContainer.shipmentId]: {
                  ...state.shipmentHash[originalContainer.shipmentId],
                  containerIds: state.shipmentHash[originalContainer.shipmentId].containerIds.filter(
                    x => x !== action.payload.shipmentContainerId
                  )
                }
              }
            : state.shipmentHash
      };
    case keys.REMOVE_SHIPMENT_ITEMS:
      const updatedContainerHash = action.payload.shipmentItemIds.reduce<Record<number, i.IShipmentContainer>>(
        (hash, id) => {
          const container = state.itemHash[id] ? hash[state.itemHash[id].shipmentContainerId] : undefined;

          if (container) {
            hash[container.shipmentContainerId] = {
              ...hash[container.shipmentContainerId],
              shipmentItemIds: hash[container.shipmentContainerId].shipmentItemIds.filter(x => x !== id)
            };
          }

          return hash;
        },
        { ...state.containerHash }
      );
      return {
        ...state,
        containerHash: updatedContainerHash,
        itemHash: omit(state.itemHash, action.payload.shipmentItemIds)
      };
    case keys.SET_CARRIERS:
      return {
        ...state,
        carriers: {
          ...state.carriers,
          ...keyBy(action.payload, x => x.vendorId)
        }
      };
    case keys.SET_LOGISTICS_DAILY_SUMMARIES:
      return {
        ...state,
        dailySummaryHash: {
          ...state.dailySummaryHash,
          ...keyBy(
            action.payload.map<i.ILogisticsDailySummary>(x => ({
              ...x,
              departureDate: new Date(x.departureDate).getTime()
            })),
            x => x.departureDate
          )
        }
      };
    case keys.SET_LOGISTICS_FILTERS:
      return {
        ...state,
        [`${action.payload.type}Filters`]: action.payload.filters
      };
    case keys.SET_LOAD:
      const normalLoad = normalizeLoad(action.payload);
      const minLoad: i.ILoadMin = convertLoadToLoadMin(action.payload);
      return {
        ...state,
        containerHash: {
          ...state.containerHash,
          ...normalLoad.containerHash
        },
        itemHash: {
          ...state.itemHash,
          ...normalLoad.itemHash
        },
        loads: { ...state.loads, [normalLoad.load.loadNumber]: normalLoad.load },
        minLoads: { ...state.minLoads, [minLoad.loadNumber]: minLoad },
        optimisticLoadUpdates: omit(state.optimisticLoadUpdates, action.payload.loadNumber),
        shipmentHash: {
          ...state.shipmentHash,
          ...normalLoad.shipments
        }
      };
    case keys.SET_LOAD_EVENTS:
      return {
        ...state,
        events: {
          ...state.events,
          ...groupBy(action.payload, x => x.loadNumber)
        }
      };
    case keys.SET_LOAD_FINAL_DESTINATION:
      if (!state.loads[action.payload.loadNumber]) {
        return state;
      }

      return {
        ...state,
        loads: {
          ...state.loads,
          [action.payload.loadNumber]: {
            ...state.loads[action.payload.loadNumber]!,
            finalDestinationShipmentId: action.payload.shipmentId
          }
        }
      };
    case keys.SET_LOAD_ROUTE:
      return {
        ...state,
        routes: {
          ...state.routes,
          [action.payload.loadNumber]: action.payload
        }
      };
    case keys.SET_MIN_LOADS:
      return {
        ...state,
        minLoads: {
          ...state.minLoads,
          ...keyBy(action.payload, x => x.loadNumber)
        }
      };
    case keys.SET_LOAD_PARTIAL:
      return {
        ...state,
        loads: {
          ...state.loads,
          [action.payload.loadNumber]: state.loads[action.payload.loadNumber]
            ? { ...(state.loads[action.payload.loadNumber] as i.ILoadNormalized), ...action.payload }
            : undefined
        }
      };
    case keys.SET_LOAD_VALUE:
      const selectedLoad = state.loads[action.payload.loadNumber];
      if (selectedLoad) {
        return {
          ...state,
          loads: {
            ...state.loads,
            [selectedLoad.loadNumber]: {
              ...selectedLoad,
              loadValue: action.payload.value
            }
          }
        };
      }
      return state;
    case keys.SET_SHIPMENT_CONTAINER:
      return {
        ...state,
        containerHash: state.containerHash[action.payload.shipmentContainerId]
          ? {
              ...state.containerHash,
              [action.payload.shipmentContainerId]: {
                ...state.containerHash[action.payload.shipmentContainerId],
                name: action.payload.name,
                shipmentContainerTypeId: action.payload.shipmentContainerTypeId,
                shipmentItemIds: (action.payload as i.IShipmentContainerWithItems).shipmentItems
                  ? (action.payload as i.IShipmentContainerWithItems).shipmentItems.map(x => x.shipmentItemId)
                  : state.containerHash[action.payload.shipmentContainerId].shipmentItemIds
              }
            }
          : {
              ...state.containerHash,
              [action.payload.shipmentContainerId]: {
                ...action.payload,
                shipmentItemIds: (action.payload as i.IShipmentContainerWithItems).shipmentItems
                  ? (action.payload as i.IShipmentContainerWithItems).shipmentItems.map(x => x.shipmentItemId)
                  : []
              }
            },
        shipmentHash: state.shipmentHash[action.payload.shipmentId]
          ? {
              ...state.shipmentHash,
              [action.payload.shipmentId]: {
                ...state.shipmentHash[action.payload.shipmentId],
                containerIds: uniqPrimativeMerge(
                  state.shipmentHash[action.payload.shipmentId].containerIds,
                  action.payload.shipmentContainerId
                )
              }
            }
          : state.shipmentHash
      };
    case keys.SET_SHIPMENT_ITEMS:
      const updates = action.payload.reduce<Pick<ILogisticsState, 'containerHash' | 'itemHash'>>(
        (update, item) => {
          const oldContainerId = state.itemHash[item.shipmentItemId]
            ? state.itemHash[item.shipmentItemId].shipmentContainerId
            : -1;

          if (update.containerHash[oldContainerId]) {
            update.containerHash[oldContainerId] = {
              ...update.containerHash[oldContainerId],
              shipmentItemIds: update.containerHash[oldContainerId].shipmentItemIds.filter(
                x => x !== item.shipmentItemId
              )
            };
          }

          if (update.containerHash[item.shipmentContainerId]) {
            update.containerHash[item.shipmentContainerId] = {
              ...update.containerHash[item.shipmentContainerId],
              shipmentItemIds: [...update.containerHash[item.shipmentContainerId].shipmentItemIds, item.shipmentItemId]
            };
          }

          update.itemHash[item.shipmentItemId] = item;
          return update;
        },
        {
          containerHash: { ...state.containerHash },
          itemHash: { ...state.itemHash }
        }
      );

      return {
        ...state,
        containerHash: updates.containerHash,
        itemHash: updates.itemHash
      };
    case keys.SET_SHIPMENT_LOADSTATE:
      const s: i.IShipment | undefined = state.shipmentHash[action.payload.shipmentId]
        ? { ...state.shipmentHash[action.payload.shipmentId], loadStateId: action.payload.loadStateId }
        : undefined;

      if (s) {
        return {
          ...state,
          shipmentHash: {
            ...state.shipmentHash,
            [action.payload.shipmentId]: s
          }
        };
      }

      return state;
    case keys.SET_SHIPMENT_STOP_NUMBERS:
      return {
        ...state,
        shipmentHash: {
          ...state.shipmentHash,
          ...action.payload.reduce<Record<number, i.IShipment>>((hash, item) => {
            if (state.shipmentHash[item.shipmentId]) {
              hash[item.shipmentId] = { ...state.shipmentHash[item.shipmentId], stopNumber: item.stopNumber };
            }
            return hash;
          }, {})
        }
        // currently doesn't use stopNumber
        // shipmentListItemHash: {
        //   ...state.shipmentListItemHash,
        //   ...action.payload.reduce<Record<number, i.IShipmentListItem>>((hash, item) => {
        //     if (state.shipmentListItemHash[item.shipmentId]) {
        //       hash[item.shipmentId] = {...state.shipmentListItemHash[item.shipmentId], }
        //     }
        //     return hash;
        //   }, {})
        // }
      };
    case keys.TOGGLE_SHIPMENT_LIST_LOADING:
      return {
        ...state,
        loading: state.loading.includes(action.payload)
          ? state.loading.filter(x => x !== action.payload)
          : [...state.loading, action.payload]
      };
    case keys.UPDATE_LOAD:
      const currentUpdate = state.optimisticLoadUpdates[action.payload.loadNumber] ?? {};
      return {
        ...state,
        optimisticLoadUpdates: {
          ...state.optimisticLoadUpdates,
          [action.payload.loadNumber]: { ...currentUpdate, ...action.payload.update }
        }
      };
    default:
      return state;
  }
}

interface INormalizedLoad {
  load: i.ILoadNormalized;
  shipments: Record<number, i.IShipment>;
  containerHash: Record<number, i.IShipmentContainer>;
  itemHash: Record<string, i.IShipmentItem>;
}

interface INormalizedShipment {
  shipment: i.IShipment;
  containerHash: Record<number, i.IShipmentContainer>;
  itemHash: Record<number, i.IShipmentItem>;
}

function removeShipment(
  state: ILogisticsState,
  { shipmentIds, loadNumber }: { shipmentIds: number[]; loadNumber: string }
): ILogisticsState {
  const loads = { ...state.loads };
  const load = loads[loadNumber];
  const minLoads = { ...state.minLoads };

  if (load) {
    loads[loadNumber] = {
      ...load,
      shipmentIds: load.shipmentIds.filter(x => !shipmentIds.includes(x))
    };
  }

  if (minLoads[loadNumber]) {
    minLoads[loadNumber] = {
      ...minLoads[loadNumber],
      shipments: minLoads[loadNumber].shipments.filter(x => !shipmentIds.includes(x.shipmentId))
    };
  }

  return {
    ...state,
    loads,
    minLoads,
    shipmentHash: omit(state.shipmentHash, shipmentIds)
  };
}

function normalizeShipment(shipment: i.IShipmentApiResponse): INormalizedShipment {
  const { containers, ...base } = shipment;

  const containerIds: number[] = [];
  const newContainers: i.IShipmentContainer[] = [];
  const items: i.IShipmentItem[] = [];
  if (containers) {
    containers.forEach(({ name, shipmentItems, shipmentContainerId, shipmentId, shipmentContainerTypeId }) => {
      containerIds.push(shipmentContainerId);
      newContainers.push({
        name,
        shipmentContainerId,
        shipmentContainerTypeId,
        shipmentId,
        shipmentItemIds: shipmentItems.map(x => x.shipmentItemId)
      });

      items.push(...shipmentItems);
    });
  }

  return {
    containerHash: keyBy(newContainers, (x: i.IShipmentContainer) => x.shipmentContainerId),
    itemHash: keyBy(items, (item: i.IShipmentItem) => item.shipmentItemId),
    shipment: {
      ...base,
      containerIds
    }
  };
}

function convertLoadToLoadMin(load: i.ILoadApiResponse): i.ILoadMin {
  return {
    carrierId: load.carrierId,
    closed: Boolean(load.closed),
    departureDate: load.departureDate,
    loadNumber: load.loadNumber,
    loadStateId: load.loadStateId,
    loadTypeId: load.loadTypeId,
    shipments: load.shipments.map<i.ILoadMinShipment>(x => ({
      accountNum: x.accountNum,
      companyId: x.companyId,
      destinationWarehouseId: x.destinationWarehouseId,
      loadNumber: x.loadNumber,
      location: `${x.city}, ${x.state}`,
      originWarehouseId: x.originWarehouseId,
      shipmentId: x.shipmentId,
      stopNumber: x.stopNumber
    }))
  };
}

function normalizeLoad(load: i.ILoadApiResponse): INormalizedLoad {
  const { shipments, ...base } = load;
  const result: INormalizedLoad = {
    containerHash: {},
    itemHash: {},
    load: {
      ...base,
      shipmentIds: shipments.map(x => x.shipmentId)
    },
    shipments: {}
  };

  shipments.forEach(s => {
    const { containerHash, itemHash, shipment } = normalizeShipment(s);

    result.containerHash = {
      ...result.containerHash,
      ...containerHash
    };

    result.itemHash = {
      ...result.itemHash,
      ...itemHash
    };

    result.shipments = {
      ...result.shipments,
      [s.shipmentId]: shipment
    };
  });

  return result;
}
